/**
 * Scrubs secret-shaped values out of text before it leaves the browser.
 *
 * Two concrete leaks make this load-bearing rather than defensive:
 * `libs/oauth/src/react-guard.tsx` throws `JSON.stringify(sess)` — the whole
 * auth session — as an unhandled rejection no React boundary can catch; and
 * `getBlobReadUrl` returns Azure SAS URLs whose `sig=` is a live credential.
 */

/** Matches monopy's `RedactSecretsFilter`. */
const REDACTED = '***REDACTED***';

/** `JSON.stringify` that survives cycles and throwing getters. */
const safeStringify = (value: unknown): string => {
  if (value == null) {
    return String(value);
  }
  try {
    return typeof value === 'object' ? (JSON.stringify(value) ?? String(value)) : String(value);
  } catch {
    return String(value);
  }
};

/**
 * `key=value`, `key: value` and `"key": "value"` secrets.
 *
 * Broader than monopy's `_KEY_VALUE_SECRET`, which was written for query params
 * and headers and cannot match `"refreshToken":"abc"` — no word boundary inside
 * `refreshToken`, and the key's closing quote sits before the `:`. Since the
 * react-guard throw is exactly that shape, the key is matched as *containing* a
 * secret word, catching `refreshToken`, `clientSecret`, `apiKey` and friends.
 * Over-matching is the safe direction.
 *
 * The lookaheads stop it re-redacting a value {@link AUTH_SCHEME} already handled.
 */
const SECRET_KEY_VALUE =
  /([A-Za-z0-9_-]*(?:token|key|secret|password|passwd|pwd|authorization|credential)[A-Za-z0-9_-]*)(["']?\s*[:=]\s*)(["']?)(?!\*\*\*REDACTED)(?!(?:Bearer|Basic|Digest|Negotiate)\b)([^&\s"',}\]]+)/gi;

/**
 * Credential-carrying auth schemes. Generalises monopy's Bearer-only pattern:
 * `authorization=Basic <base64>` would otherwise keep its payload, because the
 * key/value pattern stops at the space and captures only the scheme name.
 */
const AUTH_SCHEME = /\b(Bearer|Basic|Digest|Negotiate)\s+[A-Za-z0-9._~+/=-]+/gi;

/** SAS params, anchored to `?`/`&` so the short ones can't fire on prose. */
const SAS_PARAM = /([?&](?:sig|sv|se|st|sp|sr|ss|srt|spr|skoid|sktid)=)[^&\s'"]+/gi;

/** Bare JWTs. Signature segment is `*` so unsigned (`alg: none`) tokens match. */
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;

/**
 * Redacts secret-shaped substrings. Never throws — callers are already failing.
 *
 * Scheme patterns run first: otherwise `Authorization: Bearer abc` has `Bearer`
 * captured as the key/value pattern's value, leaving the token uncaught.
 */
export const redact = (value: unknown): string => {
  const text = typeof value === 'string' ? value : safeStringify(value);

  return text
    .replace(AUTH_SCHEME, `$1 ${REDACTED}`)
    .replace(SECRET_KEY_VALUE, `$1$2$3${REDACTED}`)
    .replace(SAS_PARAM, `$1${REDACTED}`)
    .replace(JWT, REDACTED);
};

/**
 * Drops every query-string value while keeping its keys, mirroring core-api's
 * habit of logging param keys and never values. Blanket-dropping rather than
 * matching known keys covers SAS params and OAuth codes without upkeep.
 *
 * Does **not** template path segments — routes embed `projectId`/`serviceName`,
 * so a path is itself client-identifying. A sink that collects page views must
 * switch them off rather than rely on sanitising here.
 */
export const redactUrl = (value: unknown): string => {
  const raw = typeof value === 'string' ? value : safeStringify(value);

  // `new URL` throws on the relative URLs common in error payloads.
  let parsed: URL;
  try {
    parsed = new URL(raw, 'https://redacted.invalid');
  } catch {
    return redact(raw);
  }

  for (const key of Array.from(parsed.searchParams.keys())) {
    parsed.searchParams.set(key, REDACTED);
  }

  const { search, pathname, origin } = parsed;
  return raw.startsWith('http') ? `${origin}${pathname}${search}` : `${pathname}${search}`;
};
